import { useCallback, useRef, useState, type ReactElement } from "react";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

import { ActionButton } from "src/components/v3";
import { formatReportRangeLabel, LARGE_REPORT_RANGE_MONTHS } from "src/constants/reportRange";
import { isLargeRange } from "src/utils/report/reportRange.util";

export interface LargeReportConfirm {
  /** Resolves `true` at once for a normal range; for a large one it asks the user and resolves with the answer. */
  confirmLargeRange: (fromDate: string, toDate: string) => Promise<boolean>;
  /** The dialog; render it once in the form. */
  largeRangeDialog: ReactElement;
}

/**
 * Asks "this is a large report, continue?" before a report longer than `LARGE_REPORT_RANGE_MONTHS` is generated.
 * It does not block anyone, it only gives the user a moment to pick a shorter range.
 */
export const useLargeReportConfirm = (): LargeReportConfirm => {
  const [open, setOpen] = useState(false);
  const answer = useRef<((confirmed: boolean) => void) | null>(null);

  const confirmLargeRange = useCallback((fromDate: string, toDate: string): Promise<boolean> => {
    if (!isLargeRange(fromDate, toDate)) return Promise.resolve(true);

    return new Promise<boolean>((resolve) => {
      answer.current = resolve;
      setOpen(true);
    });
  }, []);

  const close = (confirmed: boolean) => {
    setOpen(false);
    answer.current?.(confirmed);
    answer.current = null;
  };

  const largeRangeDialog = (
    <Dialog open={open} onClose={() => close(false)}>
      <DialogTitle>Large report</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This report covers more than {formatReportRangeLabel(LARGE_REPORT_RANGE_MONTHS)} and can take a while to generate. You
          can continue, or change the dates to a shorter range.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <ActionButton buttonText="Change dates" variant="outlined" onButtonClick={() => close(false)} />
        <ActionButton buttonText="Generate anyway" onButtonClick={() => close(true)} />
      </DialogActions>
    </Dialog>
  );

  return { confirmLargeRange, largeRangeDialog };
};
